<template>
  <section id="profileTrainings">
    <v-row align="center" class="mt-3" style="max-height: 56px">
      <v-col>
        <span class="title text--secondary text-uppercase">{{
          $t("profile.subheader.extTrainings")
        }}</span>
      </v-col>
      <v-col class="text-end">
        <v-btn
          v-if="
            role('safetyRepresentative')
          "
          small
          class="primary mr-2"
          @click="addExtTraining()"
        >
          <v-icon small left>note_add</v-icon>
          {{ $t("extTrainings.add") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      id="printExtTrainingsFromSection"
      class="safety-elevation-3 table-striped"
      hide-default-footer
      show-expand
      single-expand
      :items-per-page="-1"
      fixed-header
      :headers="headers"
      :items="items"
      group-by="statusGroup"
      :custom-sort="customSort"
      item-key="externalTrainingId"
      :no-data-text="$t('extTrainings.noData')"
    >
      <template
        v-slot:[`item.data-table-expand`]="{ item, expand, isExpanded }"
      >
        <v-btn
          v-if="item.extTraining.description"
          icon
          @click="expand(!isExpanded)"
          class="v-data-table__expand-icon"
          :class="{ 'v-data-table__expand-icon--active': isExpanded }"
        >
          <v-icon>mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ item }">
        <td :colspan="headers.length" style="padding-top: 16px;">
          <div v-html="`${item.extTraining.description}`"></div>
        </td>
      </template>
      <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
        <v-hover v-slot="{ hover }">
          <th
            @click="toggle"
            colspan="11"
            :style="{
              backgroundColor: hover ? '#e0e0e0' : '#eeeeee',
              cursor: hover ? 'pointer' : 'auto',
            }"
          >
            <v-row>
              <v-col cols="auto">
                <v-btn small class="primary mr-5 font" style="cursor: pointer;">
                  {{ isOpen ? $t("dialog.hide") : $t("dialog.open") }}
                  <v-icon style="margin-right: -9px;">
                    {{ isOpen ? "mdi-menu-down" : "mdi-menu-right" }}
                  </v-icon>
                </v-btn>
                <span
                  v-if="items.length"
                  class="body-2 font-weight-medium text--secondary"
                  >{{
                    items[0].statusGroup === "completed"
                      ? $t("extTrainings.completed")
                      : $t("extTrainings.upcoming")
                  }}</span
                >
              </v-col>
            </v-row>
          </th>
        </v-hover>
      </template>

      <template v-slot:[`item.extTraining.dueInMonths`]="{ item }">
        {{
          item.extTraining.dueInMonths === 0
            ? "-"
            : item.extTraining.dueInMonths
        }}
      </template>

      <template v-slot:[`item.extTraining.dateTimeOfOccurrence`]="{ item }">
        {{ item.extTraining.dateTimeOfOccurrence | date }}
      </template>

      <template v-slot:[`item.statusChip`]="{ item }">
        <v-chip
          v-if="
            isInThePast(item.extTraining.dateTimeOfOccurrence) &&
              isExpired(
                item.extTraining.dateTimeOfOccurrence,
                item.extTraining.dueInMonths
              ) &&
              item.extTraining.dueInMonths !== 0 &&
              item.participated
          "
          small
          class="text--darken-1 text-uppercase mr-1 font-weight-regular"
        >
          ✔
        </v-chip>
        <v-chip
          v-else-if="
            isInThePast(item.extTraining.dateTimeOfOccurrence) &&
              item.participated
          "
          color="green lighten-4"
          small
          class="success--text text--darken-1 text-uppercase mr-1 font-weight-regular"
        >
          ✔
        </v-chip>

        <v-chip
          v-else-if="
            isInThePast(item.extTraining.dateTimeOfOccurrence) &&
              !item.isParticipationSet
          "
          color="orange lighten-4"
          small
          class="warning--text text--darken-1 text-uppercase mr-1 font-weight-regular"
        >
          {{ $t("employees.alert.unconfirmed") }}
        </v-chip>

        <v-chip
          v-else
          color="orange lighten-4"
          small
          class="warning--text text--darken-1 text-uppercase mr-1 font-weight-regular"
        >
          {{ item.extTraining.dateTimeOfOccurrence | date }}
        </v-chip>
      </template>

      <template v-slot:[`header.file`]="{ on }">
        <div v-on="on">
          <v-btn
            class="primary"
            @click="
              print(
                'printExtTrainingsFromSection',
                `${firstName} ${lastName} (${employeeId}) - ${$t(
                  'profile.subheader.extTrainings'
                )}`
              )
            "
            small
          >
            <v-icon>print</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.file`]="{ item }">
        <v-btn v-if="item.file != null" icon @click="viewCertificate(item)">
          <v-icon>mdi-file-certificate</v-icon>
        </v-btn>
      </template>

      <template v-slot:[`item.statusGroup`]="{ item }">
        <span v-if="item.statusGroup === 'completed'">
          {{ $t("extTrainings.completed") }}
        </span>
        <span v-else-if="item.statusGroup === 'upcoming'">
          {{ $t("extTrainings.upcoming") }}
        </span>
      </template>
    </v-data-table>
    <ext-training-edit
      :extTraining="editExtTraining"
      :employee="employee"
      @done="done"
      @created="extTrainingCreated"
      :categories="extTrainingCategories || []"
    />
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { print } from "@/utils/print";
import ExtTrainingEdit from "@/components/extTraining/ExtTrainingEdit";
import ExtTrainingCategoriesQuery from "@/graphql/queries/ExtTrainingCategories.gql";

export default {
  props: ["employee", "employeeId", "firstName", "lastName"],
  components: { ExtTrainingEdit },
  data() {
    return {
      editExtTraining: null,
    }
  },
  computed: {
    ...mapGetters(["role"]),
    headers() {
      return [
        {
          text: this.$i18n.t("extTrainings.column.name"),
          value: "extTraining.name",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t(`extTrainings.column.educator`),
          value: "extTraining.educator",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t(`extTrainings.column.location`),
          value: "extTraining.location",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t(`extTrainings.durationMinutes`),
          value: "extTraining.durationMinutes",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("extTrainings.column.date"),
          value: "extTraining.dateTimeOfOccurrence",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("extTrainings.dueInMonths"),
          value: "extTraining.dueInMonths",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("instructions.column.status"),
          value: "statusChip",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          value: "statusGroup",
          sortable: false,
        },
        {
          align: "right",
          value: "file",
          class: "text-uppercase subheading text-truncate",
          slot: "header.file",
          sortable: false,
        },
      ];
    },
    items() {
      return (
        (this.employee && this.employee.extTrainingEmployee &&
          this.employee.extTrainingEmployee.map((item) => {
            const statusGroup = this.isInThePast(
              item.extTraining.dateTimeOfOccurrence
            )
              ? "completed"
              : "upcoming";
            return {
              ...item,
              statusGroup,
            };
          })) ||
        []
      );
    },
  },
  methods: {
    print: print,

    viewCertificate(item) {
      window.open(item.file.url, "_blank");
    },

    isInThePast(dateTimeOfOccurrence) {
      return this.$moment(dateTimeOfOccurrence).isBefore(this.$moment());
    },

    isExpired(dateTimeOfOccurrence, dueInMonths) {
      const expirationDate = this.$moment(dateTimeOfOccurrence).add(
        dueInMonths,
        "months"
      );
      return this.$moment().isAfter(expirationDate);
    },
    customSort(items) {
      return items.sort((a, b) => {
        const aIsPast = this.isInThePast(a.extTraining.dateTimeOfOccurrence);
        const bIsPast = this.isInThePast(b.extTraining.dateTimeOfOccurrence);

        if (aIsPast && bIsPast) {
          return (
            new Date(b.extTraining.dateTimeOfOccurrence) -
            new Date(a.extTraining.dateTimeOfOccurrence)
          );
        } else if (!aIsPast && !bIsPast) {
          return (
            new Date(a.extTraining.dateTimeOfOccurrence) -
            new Date(b.extTraining.dateTimeOfOccurrence)
          );
        } else {
          return aIsPast ? 1 : -1;
        }
      });
    },
    addExtTraining() {
      this.editExtTraining = {
        categoryId: null,
        name: "",
        dateTimeOfOccurrence: new Date().toISOString(),
        educator: "",
        location: "",
        durationMinutes: 90,
        dueInMonths: null,
        employees: [],
        description: "",
      };
    },
    done() {
      this.editExtTraining = null;
    },
    extTrainingCreated() {
      this.editExtTraining = null;
      this.$emit('refresh')     
    }
  },
  apollo: {
    extTrainingCategories: {
      query: ExtTrainingCategoriesQuery,
      skip() {
        return !this.editExtTraining
      }
    },
  },
};
</script>
